import moment from 'moment';

import {
    getWeekdayName as getWeekdayNameByIndex,
    getWeekdayShortName as getWeekdayShortNameByIndex,
} from './consts';
import {
    Weekday,
    CalendarDate,
    Time,
    DateTime,
    Timestamp,
    ISOMonth,
    ISOCalendarDate,
    ISODateRange,
    ISODateTime,
    ISODateTimeRange,
    ISOTime
} from './types';

const INPUT_DATE_FORMAT = 'DD.MM.YYYY';
const MONTH_FORMAT = 'YYYY-MM';
const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';
const PRECISE_TIME_FORMAT = 'HH:mm:ss';
const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
const TIMESTAMP_FORMAT = 'X';

const DATE_HUMAN_FORMAT = 'DD.MM';
const MONTH_HUMAN_TEXT_FORMAT = 'MMMM YYYY'
const DAY_HUMAN_TEXT_FORMAT = 'DD MMMM, dd';

const CONSULTATION_TIME_FORMAT = 'DD.MM.YYYY HH:mm'

export const DAYS_IN_WEEK = 7;
export const MINUTES_IN_HOUR = 60;
export const SECONDS_IN_MINUTES = 60
export const MILLISECONDS_IN_SECOND = 1000;

const RUSSIAN_LOCALE = 'ru';

export const getDateAndTime = (date: string): string => {
    return moment(date).format(CONSULTATION_TIME_FORMAT);
}

export const formatDateForBackend = (time: string, format: string) => {
    return moment(time, format).format();
}

export const formatDefaultDateToFormat = (date: string, format: string) => {
    return moment(date).format(format);
}

export const getConsultationTime = (time: string): ISOTime => {
    const momentTime = moment(time, CONSULTATION_TIME_FORMAT);

    return momentTime.format(TIME_FORMAT);
}

export const getConsultationDuration = (start: string, end: string): number => {
    const momentStart = moment(start, CONSULTATION_TIME_FORMAT);
    const momentEnd = moment(end, CONSULTATION_TIME_FORMAT);

    return momentEnd.diff(momentStart, 'minutes');
}

export const getConsultationPassedSeconds = (start: string): number => {
    const momentStart = moment(start, CONSULTATION_TIME_FORMAT);
    const momentNow = moment();

    return momentNow.diff(momentStart, 'seconds');
}

export const formatTime = (seconds: number): string => {
    const milliseconds = seconds * MILLISECONDS_IN_SECOND;
    const momentTime = moment.utc(milliseconds);

    return momentTime.format(PRECISE_TIME_FORMAT)
}

export const formatToMilliseconds = (date = '', format = '') => {
    return moment(date, format).valueOf();
}

export const getNow = () => {
    return moment().valueOf();
}

export const getDuration = (ms: number) => {
    const duration = moment.duration(ms);

    return {
        hours: Math.abs(duration.hours()),
        minutes: Math.abs(duration.minutes()),
        seconds: Math.abs(duration.seconds()),
    }
}

export const getDifference = (startTime: number, endTime: number, unit?: moment.unitOfTime.Diff) => {
    return moment(startTime).diff(endTime, unit);
}

export const normalizeTime = (time: number) => String(time).padStart(2, '0');

// timestamp

export const getCurrentTimestamp = (): Timestamp => {
    const today = moment();

    return today.unix();
}

export const formatTimestampToTime = (timestamp: Timestamp): ISOTime => {
    const momentTime = moment(timestamp, TIMESTAMP_FORMAT);

    return momentTime.format(TIME_FORMAT)
}

// month

export const getCurrentMonth = (): ISOMonth => {
    const momentDate = moment();

    return momentDate.format(MONTH_FORMAT);
}

export const getMonthFromDate = (date: ISOCalendarDate): ISOMonth => {
    const momentDate = moment(date, DATE_FORMAT);

    return momentDate.format(MONTH_FORMAT);
}

// legacy date format

const parseMomentDate = (date: moment.Moment): CalendarDate => ({
    day: date.date(),
    month: date.month() + 1,
    year: date.year(),
})

export const getCurrentDate = (): CalendarDate => {
    const today = moment();

    return parseMomentDate(today);
}

export const parseDate = (date: ISOCalendarDate): CalendarDate => {
    const momentDate = moment(date, DATE_FORMAT);

    return parseMomentDate(momentDate);
}

// date

export const normalizeInputDate = (date: string): ISOCalendarDate => {
    const momentDate = moment(date, INPUT_DATE_FORMAT);

    return momentDate.format(DATE_FORMAT);
}

export const getTodayDate = (): ISOCalendarDate => {
    const today = moment();

    return today.format(DATE_FORMAT);
}

const createHumanFormatter = <Source = ISOCalendarDate>(desiredFormat: string, sourceFormat = DATE_FORMAT) => (date: Source): string => {
    const momentDate = moment(date, sourceFormat);

    return momentDate.locale(RUSSIAN_LOCALE).format(desiredFormat);
}

export const formatDateForInput = createHumanFormatter(INPUT_DATE_FORMAT);
export const formatDateToHuman = createHumanFormatter(DATE_HUMAN_FORMAT)
export const formatMonthToHumanText = createHumanFormatter(MONTH_HUMAN_TEXT_FORMAT)
export const formatDayToHumanText = createHumanFormatter(DAY_HUMAN_TEXT_FORMAT)

export const getWeekday = (date: ISOCalendarDate): Weekday => {
    const momentDate = moment(date, DATE_FORMAT);

    return momentDate.day();
}

export const getWeekdayName = (date: ISOCalendarDate) => {
    const weekday = getWeekday(date);

    return getWeekdayNameByIndex(weekday);
}

export const getWeekdayShortName = (date: ISOCalendarDate) => {
    const weekday = getWeekday(date);

    return getWeekdayShortNameByIndex(weekday);
}

// time

const parseMomentTime = (time: moment.Moment): Time => ({
    hour: time.hour(),
    minute: time.minute(),
})

export const isTimeBefore = (start: ISOTime, from: ISOTime): boolean => {
    const momentStart = moment(start, TIME_FORMAT);
    const momentFrom = moment(from, TIME_FORMAT);

    return momentStart.isBefore(momentFrom);
}

export const parseTime = (time: ISOTime): Time => {
    const momentTime = moment(time, TIME_FORMAT);

    return parseMomentTime(momentTime);
}

export const formatMinutesToTime = (minutes: number): ISOTime => {
    const momentTime = moment().hours(0).minutes(minutes);

    return momentTime.format(TIME_FORMAT);
}

// datetime

export const getCurrentDateTime = (): ISODateTime => {
    const now = moment();

    return now.format(DATE_TIME_FORMAT);
}

export const buildDateTime = (date: ISOCalendarDate, time: ISOTime): ISODateTime => `${date} ${time}`;

export const discardDate = (datetime: ISODateTime): ISOTime => {
    const momentDatetime = moment(datetime, DATE_TIME_FORMAT);

    return momentDatetime.format(TIME_FORMAT);
}

export const discardTime = (datetime: ISODateTime): ISOCalendarDate => {
    const momentDatetime = moment(datetime, DATE_TIME_FORMAT);

    return momentDatetime.format(DATE_FORMAT);
}

export const parseDateTime = (datetime: ISODateTime): DateTime => {
    const momentDatetime = moment(datetime, DATE_TIME_FORMAT);

    return {
        ...parseMomentDate(momentDatetime),
        ...parseMomentTime(momentDatetime),
    };
}

export const setTimeWithinDay = (datetime: ISODateTime, time: Time): ISODateTime => {
    return moment(datetime, DATE_TIME_FORMAT)
        .hour(time.hour)
        .minutes(time.minute)
        .format(DATE_TIME_FORMAT);
}

export const convertMinutes = (minutes: number): Time => {
    const momentTime = moment().hour(0).minutes(minutes);

    return parseMomentTime(momentTime);
}

export const getTimeInMinutes = (datetime: ISODateTime): number => {
    const { hour, minute } = parseDateTime(datetime);

    return hour * MINUTES_IN_HOUR + minute;
}

const createDifferenceCalculator = (unit: 'minutes' | 'hours') => (entry: ISODateTimeRange) => {
    const momentStart = moment(entry.start, DATE_TIME_FORMAT);
    const momentEnd = moment(entry.end, DATE_TIME_FORMAT);

    return momentEnd.diff(momentStart, unit);
};

export const getMinutesDifference = createDifferenceCalculator('minutes');

export const getDaysDifference = (start: ISOCalendarDate, end: ISOCalendarDate) => {
    const momentStart = moment(start, DATE_FORMAT);
    const momentEnd = moment(end, DATE_FORMAT);

    return momentEnd.diff(momentStart, 'days');
}

export const formatTimeInterval = (interval: ISODateTimeRange) => {
    const momentStart = moment(interval.start, DATE_TIME_FORMAT);
    const momentEnd = moment(interval.end, DATE_TIME_FORMAT);

    const startTime = momentStart.format(TIME_FORMAT)
    const endTime = momentEnd.format(TIME_FORMAT)

    return `${startTime} - ${endTime}`;
}

export const getTodayDateString = (): ISOCalendarDate => moment().format(DATE_FORMAT);

export const normalizeIsoDate = (date: string): ISOCalendarDate => {
    return moment(date).format(DATE_FORMAT);
}

export const normalizeDate = (date: string): ISOCalendarDate => {
    return moment(date, 'DD.MM.YYYY').format(DATE_FORMAT);
}

export const getNextWeek = (date: string): ISOCalendarDate => moment(date).add(7, 'days').format(DATE_FORMAT);

export const getPreviousMonth = (date: string): ISOCalendarDate => moment(date).subtract(1, 'months').format(DATE_FORMAT);
export const getNextMonth = (date: string): ISOCalendarDate => moment(date).add(1, 'months').format(DATE_FORMAT);

export const getMaxDate = (...dates: string[]): ISOCalendarDate => {
    const momentDates = dates.map(date => moment(date));

    return moment.max(momentDates).format(DATE_FORMAT);
}

export const isSameDay = (a: ISOCalendarDate, b: ISOCalendarDate): boolean => moment(a).isSame(b, 'day');
export const isSameMonth = (a: ISOCalendarDate, b: ISOCalendarDate): boolean => moment(a).isSame(b, 'month');

// range

const getLastWeekFromPreviousMonth = (date: ISOCalendarDate): ISOCalendarDate => {
    const momentDate = moment(date, DATE_FORMAT);
    const previousMonthLastWeek = momentDate
        .date(1)
        .subtract(1, 'week');
    
    return previousMonthLastWeek.format(DATE_FORMAT)
}

const getFirstWeekFromNextMonth = (date: ISOCalendarDate): ISOCalendarDate => {
    const momentDate = moment(date, DATE_FORMAT);
    const nextMonthFirstWeek = momentDate
        .date(1)
        .add(1, 'month')
        .add(1, 'week');
    
    return nextMonthFirstWeek.format(DATE_FORMAT)
}

// calendar
// date is either first or last day of the month
// returns range from last week of previous month to first week of next month
export const getMonthAdjacentRange = (date: ISOCalendarDate): ISODateRange => { 
    return {
        start: getLastWeekFromPreviousMonth(date),
        end: getFirstWeekFromNextMonth(date),
    }
}

const getMonday = (date: ISOCalendarDate): ISOCalendarDate => {
    const momentDate = moment(date, DATE_FORMAT);
    
    return momentDate
        .isoWeekday(Weekday.Monday)
        .format(DATE_FORMAT)
}

const getSunday = (date: ISOCalendarDate): ISOCalendarDate => {
    const momentDate = moment(date, DATE_FORMAT);
    
    return momentDate
        .isoWeekday(Weekday.Sunday)
        .format(DATE_FORMAT)
}

// date is on of the days within a week
// returns range from week start to the week end
export const getWeekRange = (date: ISOCalendarDate): ISODateRange => { 
    return {
        start: getMonday(date),
        end: getSunday(date),
    }
}

export const getDayRange = (date: ISOCalendarDate): ISODateRange => {
    return {
        start: date,
        end: date,
    }
}

export const createDateRangeChecker = (range: ISODateRange) => (date: ISOCalendarDate) => {
    const momentDatetime = moment(date, DATE_TIME_FORMAT);

    // including start, excluding end
    return momentDatetime.isBetween(range.start, range.end, 'days', '[]')
}

export const createDatetimeRangeChecker = (range: ISODateTimeRange) => (datetime: ISODateTime) => {
    const momentDatetime = moment(datetime, DATE_TIME_FORMAT);

    // including start, excluding end
    return momentDatetime.isBetween(range.start, range.end, 'minute', '[)')
}

export const addMinutes = (datetime: ISODateTime, minutes: number): ISODateTime =>  {
    const momentDatetime = moment(datetime, DATE_TIME_FORMAT);
    momentDatetime.add(minutes, 'minutes');

    return momentDatetime.format(DATE_TIME_FORMAT);
}
